import BreadCrumbs from '~/shared/components/bread-crumbs';
import Content from '~/shared/components/content';
import Tags from '~/shared/components/tags';
import { PAGE_NAMES } from '~/shared/constants';
import { getLocaleLink } from '~/shared/helpers/browser-helpers';
import { getDate, getUrl } from '~/shared/helpers/string-helpers';
import type { CourseDirectionType } from '~/shared/types/company-type';
import type { PostType } from '~/shared/types/post-type';

import '~/shared/components/banner/banner.scss';

import noImage from '~/assets/no-image.webp';

type BannerPropsType = {
  post: PostType;
  t: (key: string, options: { ns: string[] }) => string;
};

const Banner = ({
  isTimeForReading = true,
  post,
  backButtonText,
  backButtonClick,
  pticeInfoBlock = null,
  lng,
  t,
  breadCrumbs,
}: BannerPropsType) => {
  const courseDirection = post?.company?.courseDirections?.find((item: CourseDirectionType) => {
    return item?._id === post?.courseDirection;
  });

  return (
    <section
      className="banner"
      style={{
        background: `url(${post?.image?.path ?? noImage.src}) center/cover no-repeat`,
      }}
    >
      <link itemProp="image" href={post?.image?.path ?? noImage.src} />
      <meta itemProp="dateModified" content={post?.createdAt} />
      <meta
        itemScope
        itemProp="mainEntityOfPage"
        itemType="https://schema.org/WebPage"
        itemID={getLocaleLink(getUrl({ link: PAGE_NAMES.POST, alias: post?.alias }), lng)}
      />
      <div className="banner__container-wrapper">
        <Content>
          <div className="banner__container">
            <BreadCrumbs className="banner__breadcrumbs" t={t} links={breadCrumbs} />
            <div className="banner__top-navigation">
              <div onClick={backButtonClick} className="banner__return-to-blog">
                {backButtonText}
              </div>
              {!post?.courseDirection && (post?.tags ?? []).length > 0 && (
                <div className="banner__tags-block">
                  <Tags isWhite lng={lng} tags={post?.tags} />
                </div>
              )}
              {post?.courseDirection && (
                <div className="banner__direction-block">
                  <div itemProp="author" className="banner__user-name">
                    {t('text.course-direction')}:
                  </div>
                  <div
                    className="banner__direction"
                    style={{
                      background: courseDirection?.backgroundColor,
                      color: courseDirection?.color,
                      borderColor: courseDirection?.color,
                    }}
                  >
                    {courseDirection?.courseDirectionName}
                  </div>
                </div>
              )}
            </div>
            <h1 itemProp="headline" className="banner__title">
              {post?.title}
            </h1>
            {post?.description && (
              <div
                itemProp="description"
                className="banner__short-description-text"
                suppressHydrationWarning
                dangerouslySetInnerHTML={{
                  __html: post?.description ?? '',
                }}
              />
            )}
            <div className="banner__post-info">
              <div className="banner__user-block">
                <div className="banner__user-name">
                  <div
                    style={{
                      background: `url(${post?.creator?.avatar?.path}) center/cover no-repeat`,
                    }}
                    className="banner__user-avatar"
                  />
                  <div className="banner__user-block-container">
                    <div itemProp="author" className="banner__user-name">
                      {t('text.author')}
                    </div>
                    <div itemProp="author" className="banner__user-name">
                      {post?.creator ? `${post?.creator?.firstName} ${post?.creator?.lastName}` : t('text.no-author')}
                    </div>
                    {isTimeForReading && (
                      <div itemProp="timeRequired" className="banner__time-to-read">
                        {post?.timeForReading ?? '-'}&nbsp;
                        {t('text.time-for-reading')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="banner__additional-info">
                <div className="banner__date">
                  <span className="banner__creation-date">{t('text.creation-date')}:</span>
                  <time itemProp="datePublished" dateTime={post?.createdAt}>
                    {getDate(post?.createdAt)}
                  </time>
                </div>
                {pticeInfoBlock}
              </div>
            </div>
          </div>
        </Content>
      </div>
    </section>
  );
};

export default Banner;
